// Fase 17.3, Plano 08 — cliente HTTP da gestão de modelos de roteiro (D-01, D-17).
import type {
  AtualizarCabecalhoModeloDto,
  CriarModeloRoteiroDto,
  ImpactoModeloRoteiroDto,
  ModeloRoteiroResumoDto,
  ResultadoCabecalhoModeloDto,
  ResultadoModeloRoteiroDto,
  ResultadoVoltarAoModeloDto,
  SalvarModeloRoteiroDto,
  VoltarAoModeloDto,
} from '@pieltec/contracts';
import { apiFetch, apiMutate } from '../csrf-fetch';
import { lerErroApi } from '../vendas/erro-api';
import { ErroRoteiroApi } from './roteiros-api';

const BASE = '/api/v1/roteiros/modelos';

export const MENSAGEM_CONFLITO_MODELO = 'Outra pessoa alterou este modelo. Recarregue e tente de novo.';

/** Modelos para a tela de gestão; `incluirInativos` mostra também os desativados. */
export function listarModelosDaGestao(incluirInativos = false): Promise<ModeloRoteiroResumoDto[]> {
  return apiFetch<ModeloRoteiroResumoDto[]>(`${BASE}${incluirInativos ? '?inativos=true' : ''}`);
}

/** Quantas peças receberiam a nova versão e quantas diferem do modelo (mostrado antes de salvar). */
export function impactoDoModelo(modeloId: string): Promise<ImpactoModeloRoteiroDto> {
  return apiFetch<ImpactoModeloRoteiroDto>(`${BASE}/${encodeURIComponent(modeloId)}/impacto`);
}

/** Salva a versão nova do modelo (PUT); a API propaga às peças que o seguem na mesma transação. */
export async function alterarModelo(modeloId: string, corpo: SalvarModeloRoteiroDto): Promise<ResultadoModeloRoteiroDto> {
  const resposta = await apiMutate(`${BASE}/${encodeURIComponent(modeloId)}`, { method: 'PUT', body: corpo });
  if (!resposta.ok) {
    if (resposta.status === 409) throw new ErroRoteiroApi(MENSAGEM_CONFLITO_MODELO, 409);
    if (resposta.status === 403) throw new ErroRoteiroApi('Você não tem permissão para editar modelos de roteiro.', 403);
    const erro = await lerErroApi(resposta, 'Não foi possível salvar o modelo. Tente novamente.');
    throw new ErroRoteiroApi(erro.mensagem, resposta.status);
  }
  return (await resposta.json()) as ResultadoModeloRoteiroDto;
}

/** Cria um modelo do PCP (código MP-NNNN gerado pelo servidor). */
export async function criarModelo(corpo: CriarModeloRoteiroDto): Promise<ResultadoModeloRoteiroDto> {
  const resposta = await apiMutate(BASE, { method: 'POST', body: corpo });
  if (!resposta.ok) {
    if (resposta.status === 403) throw new ErroRoteiroApi('Você não tem permissão para criar modelos de roteiro.', 403);
    const erro = await lerErroApi(resposta, 'Não foi possível criar o modelo. Tente novamente.');
    throw new ErroRoteiroApi(erro.mensagem, resposta.status);
  }
  return (await resposta.json()) as ResultadoModeloRoteiroDto;
}

/** Renomeia e/ou ativa/desativa um modelo (PATCH); só as chaves enviadas mudam. */
export async function atualizarCabecalhoDoModelo(modeloId: string, alteracoes: AtualizarCabecalhoModeloDto): Promise<ResultadoCabecalhoModeloDto> {
  const resposta = await apiMutate(`${BASE}/${encodeURIComponent(modeloId)}`, { method: 'PATCH', body: alteracoes });
  if (!resposta.ok) {
    if (resposta.status === 403) throw new ErroRoteiroApi('Você não tem permissão para editar modelos de roteiro.', 403);
    const erro = await lerErroApi(resposta, 'Não foi possível atualizar o modelo. Tente novamente.');
    throw new ErroRoteiroApi(erro.mensagem, resposta.status);
  }
  return (await resposta.json()) as ResultadoCabecalhoModeloDto;
}

/** "Voltar a seguir o modelo": a API cria uma versão da peça com as etapas do modelo. */
export async function voltarAoModelo(materialId: string, corpo: VoltarAoModeloDto): Promise<ResultadoVoltarAoModeloDto> {
  const resposta = await apiMutate(`/api/v1/roteiros/pecas/${encodeURIComponent(materialId)}/roteiro/voltar-ao-modelo`, { method: 'POST', body: corpo });
  if (!resposta.ok) {
    if (resposta.status === 403) throw new ErroRoteiroApi('Você não tem permissão para editar roteiros.', 403);
    const erro = await lerErroApi(resposta, 'Não foi possível voltar a seguir o modelo. Tente novamente.');
    throw new ErroRoteiroApi(erro.mensagem, resposta.status);
  }
  return (await resposta.json()) as ResultadoVoltarAoModeloDto;
}
